import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';

/**
 * 「フィードバックを送る」（2026-10-03 前田さん要望: プリセットボタンがあると報告しやすい）。
 * 送ると、直前のやりとりつきでスタッフ全員にメールし、開発側が読む会話記録にも残る。
 */
export const FEEDBACK_CATEGORIES = ['誤り・おかしい', 'こうしてほしい', '良かった', 'その他'] as const;
export type FeedbackCategory = typeof FEEDBACK_CATEGORIES[number];

interface Props {
  open: boolean;
  busy?: boolean;
  onClose: () => void;
  onSubmit: (category: FeedbackCategory, text: string) => void;
}

export default function AgentFeedbackDialog({ open, busy, onClose, onSubmit }: Props) {
  const [category, setCategory] = useState<FeedbackCategory>('誤り・おかしい');
  const [text, setText] = useState('');
  const submit = () => {
    if (!text.trim()) return;
    onSubmit(category, text.trim());
    setText('');
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontSize: 16, fontWeight: 800 }}>📝 フィードバックを送る</DialogTitle>
      <DialogContent>
        <ToggleButtonGroup
          size="small" exclusive value={category}
          onChange={(_e, v) => v && setCategory(v)}
          sx={{ flexWrap: 'wrap', mb: 1.2 }}
        >
          {FEEDBACK_CATEGORIES.map((c) => (
            <ToggleButton key={c} value={c} sx={{ py: 0.3, px: 1.2, textTransform: 'none' }}>{c}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField
          autoFocus fullWidth multiline minRows={3}
          label="どこが・どうだったか"
          placeholder="例: 予約経路が英語サイトなのに日本語でメールを作った"
          value={text}
          onChange={(e) => setText(e.target.value)}
          // iOS Safari は font-size 16px 未満で自動ズームするため 16px に統一
          inputProps={{ style: { fontSize: 16 } }}
        />
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
          直前のやりとりが一緒に送られ、スタッフ全員にメールが届きます。開発側の改善にも使われます。
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" sx={{ textTransform: 'none' }}>やめる</Button>
        <Button onClick={submit} variant="contained" disabled={!text.trim() || busy} sx={{ textTransform: 'none', fontWeight: 700 }}>
          送る
        </Button>
      </DialogActions>
    </Dialog>
  );
}
